import { cloneDeep, isEmpty } from 'lodash-es';
import React, { useEffect, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { ISmartFloorPayloadUI } from '~/src/dash/apiV2/smartFloors/crud';
import { ListViewTable } from '~/src/dash/components/tables/ListViewTable';
import {
  extractDefaultActiveColumnKeys,
  extractSearchableColumnKeys,
  extractVisibleColumns,
  getComparator,
  IListViewTableColumnInfo,
  IListViewTableRow,
  Order,
  RowsPerPage,
  stableSort,
} from '~/src/dash/components/tables/ListViewTable/common';
import { ColumnSelector } from '~/src/dash/components/tables/ListViewTable/linked_components/ColumnSelector';
import { borderRadius, Col, Cols, Row, Rows, spacing, TextInput } from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';
import {
  LoadingState,
  LoadingStateHasData,
  LoadingStateLoading,
  LoadingStateNoData,
} from '~/src/dash/utils/loading-states';
import { hasRoleSuperUser } from '~/src/dash/utils/roles';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import { useSmartFloorsListStore } from '~/src/dash/views/bidders/Bidders/SmartFloors/contexts';
import { formatSmartFloorsResponseIntoLVTRows } from '~/src/dash/views/bidders/Bidders/SmartFloors/SmartFloorsListView/config';
import { ISmartFloorsListViewConfig } from '~/src/dash/views/bidders/Bidders/SmartFloors/SmartFloorsListView/config';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

const queryFilteredData = (
  filteredData: ISmartFloorPayloadUI[],
  query: string,
  searchableColumnKeys: string[],
) => {
  let queriedRows = cloneDeep(filteredData);
  if (query !== '') {
    queriedRows = filteredData.filter((row) => {
      for (const key of searchableColumnKeys) {
        if (
          row[key as keyof ISmartFloorPayloadUI]
            ?.toString()
            .toLowerCase()
            .includes(query.toLowerCase())
        ) {
          return true;
        }
      }
      return false;
    });
  }
  return queriedRows;
};

export const SmartFloorsListView = () => {
  const { user } = useBaseStore();
  const { readAuthorization, writeAuthorization, SmartFloors, handlers, shouldAllowSortingTable } =
    useSmartFloorsListStore();
  const isSuperUser = hasRoleSuperUser(user.user);
  const cfg = SmartFloors.cfg as ISmartFloorsListViewConfig;
  const { cypressClassName, defaultState } = cfg;
  cfg.isInitialized = true;

  // states and refs
  const isMounted = useRef(false);
  const [responseData, setResponseData] = useState<ISmartFloorPayloadUI[]>([]);
  const [queriedData, setQueriedData] = useState<ISmartFloorPayloadUI[]>([]);
  const [prevSortedRows, setPrevSortedRows] = useState([] as IListViewTableRow[]);
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [sortedRows, setSortedRows] = useState([] as IListViewTableRow[]);

  const [loadState, setLoadState] = useState<LoadingState>(LoadingStateLoading);

  // search
  const [query, setQuery] = useState('');

  // table states
  const [activeColumnKeys, setActiveColumnKeys] = useState<string[]>(
    defaultState.activeColumnKeys || extractDefaultActiveColumnKeys(cfg.table.columns),
  );
  const [order, setOrder] = useState<Order>(defaultState.order || 'asc');
  const [orderBy, setOrderBy] = useState<string>(defaultState.orderBy || 'Name');
  const [allowSorting, setAllowSorting] = useState<boolean>(shouldAllowSortingTable); // prop will be false if it's a change to TextInput
  const [page, setPage] = useState(defaultState.page || 0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultState.rowsPerPage || 10);

  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarError, setSnackbarError] = useState(false);

  // other consts
  const searchableColumnKeys = extractSearchableColumnKeys(cfg.table.columns);

  useEffect(() => {
    getData();
  }, [SmartFloors.data]);

  // filter data via search query
  useEffect(() => {
    if (isMounted.current) {
      const queriedRows = queryFilteredData(responseData, query, searchableColumnKeys);
      unstable_batchedUpdates(() => {
        setQueriedData(queriedRows);
        setTotalRowCount(queriedRows.length);
        setPage(cfg.isInitialized ? defaultState.page : 0);
        setLoadState(!isEmpty(queriedRows) ? LoadingStateHasData : LoadingStateNoData);
      });
    }
  }, [responseData, query]);

  // sort queriedData via column or sort bys
  useEffect(() => {
    if (isMounted.current) {
      if (allowSorting) {
        if (!isEmpty(queriedData)) {
          setSortedRows(
            stableSort(rows, getComparator(order, orderBy, cfg.table.columns[orderBy].valueType)),
          );
        } else {
          setSortedRows([]);
        }
      } else {
        // keep the sort order but update what's displayed
        const updatedPrevRows = prevSortedRows;

        queriedData.forEach((newSF) => {
          prevSortedRows.forEach((row, idx) => {
            if (row.ID.value === String(newSF.SiteID)) {
              updatedPrevRows[idx].BaseBidFloor.value = Number(newSF.BaseBidFloor);
              updatedPrevRows[idx].MaxBidFloor.value = Number(newSF.MaxBidFloor);
              updatedPrevRows[idx].MinBidFloor.value = Number(newSF.MinBidFloor);
              if (updatedPrevRows[idx].OptimizedBidFloor) {
                updatedPrevRows[idx].OptimizedBidFloor.value = Number(newSF.OptimizedBidFloor);
              }
              updatedPrevRows[idx].TestIncrementHigh.value = Number(newSF.TestIncrementHigh);
            }
          });
        });

        setSortedRows(updatedPrevRows);
      }

      cfg.defaultState.orderBy = orderBy;
      cfg.defaultState.order = order;
    }

    // allow sorting again after a row's TextInput is done updating
    setAllowSorting(true);
  }, [queriedData, order, orderBy]);

  // update config on selected columns change
  useEffect(() => {
    if (isMounted.current) {
      // @ts-ignore
      cfg.defaultState.activeColumnKeys = activeColumnKeys;
    }
  }, [activeColumnKeys]);

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
    }
  }, []);

  // get the data from SmartFloorsListStore and set into state
  const getData = () => {
    if (readAuthorization.isAuthorized) {
      setLoadState(LoadingStateLoading);
      const smartFloors = SmartFloors.data as ISmartFloorPayloadUI[];

      unstable_batchedUpdates(() => {
        setPrevSortedRows(cloneDeep(sortedRows)); // set previous state of sorted rows for later
        setResponseData(smartFloors);
        setLoadState(isEmpty(smartFloors) ? LoadingStateNoData : LoadingStateHasData);
        setAllowSorting(shouldAllowSortingTable);
      });
    }
  };

  // this sets up the changes to force a re-sort
  const handleOnClickColumnHeader = (column: IListViewTableColumnInfo) => {
    const { key, defaultOrderDirection } = column;
    if (orderBy !== key) {
      unstable_batchedUpdates(() => {
        setOrder(defaultOrderDirection || 'asc');
        setOrderBy(key);
        setPage(0);
      });
    } else {
      const isAsc = orderBy === key && order === 'asc';
      unstable_batchedUpdates(() => {
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(key);
        setPage(0);
      });
    }
  };

  const handleChangePage = (_e: unknown, newPage: number) => {
    setPage(newPage);
    cfg.defaultState.page = newPage;
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = Number(event.target.value) as RowsPerPage;
    unstable_batchedUpdates(() => {
      setRowsPerPage(newRowsPerPage);
    });
    cfg.defaultState.rowsPerPage = newRowsPerPage;
  };

  const handleOnChangeQuery = (text: string) => {
    unstable_batchedUpdates(() => {
      setLoadState(LoadingStateLoading);
      setQuery(text);
    });
  };

  const handleChangeActiveColumns = (keys: string[]) => {
    setActiveColumnKeys(keys);
  };

  const handleSnackbarClose = () => {
    setSnackbarMessage('');
  };

  const handleErrorWithSnackBar = (err: string) => {
    unstable_batchedUpdates(() => {
      setSnackbarError(true);
      setSnackbarMessage(err);
    });
  };

  const rows = formatSmartFloorsResponseIntoLVTRows(queriedData, cfg.table);

  // since all data is collected, we handle the pagination here
  const visibleRows = sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Rows
      basePadding
      style={{
        background: publicaColors.white,
        border: `1px solid ${publicaColors.lightGray}`,
        borderRadius: borderRadius.large,
      }}
    >
      <Row noPadding paddingBottom={spacing.large}>
        <Cols
          style={{
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Col>
            <TextInput
              enableClear
              handleOnChange={handleOnChangeQuery}
              msDelay={300}
              placeholder='Search by Name or Site ID'
              prefix='search'
            />
          </Col>
          <Col>
            <ColumnSelector
              // simple multi select props
              preselectedColumnKeys={cfg.defaultState.activeColumnKeys}
              resetActiveColumnKeys={cfg.columnSelector.resetKeys}
              columnsList={extractVisibleColumns(
                cfg.table.columns,
                activeColumnKeys || [],
                isSuperUser,
              )}
              columnsHandleOnChangeOption={handleChangeActiveColumns}
              columnsHandleOnAccept={handleChangeActiveColumns}
            />
          </Col>
        </Cols>
      </Row>
      <Row noPadding>
        <ListViewTable
          activeColumnKeys={activeColumnKeys}
          columns={cfg.table.columns}
          customProps={{
            selectedSiteIDForChart: SmartFloors.selectedSiteIDForSmartFloorsChart,
          }}
          customFunctions={{
            handleSelectSiteOnChart: handlers.handleSelectSiteOnChart,
            handleChangePriceText: handlers.handleUpdateBidFloorForSmartFloor,
            handleErrorWithSnackBar,
          }}
          enableActions
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          displayNameWithID
          enableCustomLeftColumn
          handleOnClickColumnHeader={handleOnClickColumnHeader}
          isReadAuthorized={readAuthorization.isAuthorized}
          isSuperUser={isSuperUser}
          isWriteAuthorized={writeAuthorization.isAuthorized}
          loadState={loadState}
          order={order}
          orderBy={orderBy}
          page={page}
          rows={visibleRows}
          rowsPerPage={rowsPerPage}
          totalRowCount={totalRowCount}
          className={cypressClassName}
        />
      </Row>

      {/* snackbar is a special case and can act as a panel */}
      <SnackbarView open={!!snackbarMessage} error={!!snackbarError} onClose={handleSnackbarClose}>
        {snackbarMessage}
      </SnackbarView>
    </Rows>
  );
};
